import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../api";
import { Bell, X, Check, CheckCheck, Loader2 } from "lucide-react";

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const res = await api.get("/notifications/unread-count");
      setUnreadCount(res.data?.count || 0);
    } catch { /* silent */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/notifications/");
      setNotifications(res.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [user, fetchCount]);

  const handleOpen = () => {
    setOpen(!open);
    if (!open) fetchNotifications();
  };

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => n.notification_id === id ? { ...n, is_read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  const typeIcon = (type) => {
    const colors = {
      emi_reminder: "bg-blue-100 text-blue-600",
      emi_due_today: "bg-amber-100 text-amber-600",
      emi_overdue: "bg-red-100 text-red-600",
      agent_alert: "bg-orange-100 text-orange-600",
      payment_submitted: "bg-green-100 text-green-600",
      payment_verified: "bg-emerald-100 text-emerald-600",
      payment_rejected: "bg-red-100 text-red-600",
      system: "bg-slate-100 text-slate-600",
    };
    return colors[type] || "bg-slate-100 text-slate-600";
  };

  const getNotifUrl = (n) => {
    if (["payment_submitted"].includes(n.notification_type)) return "/app/payment-verification";
    if (["payment_verified", "payment_rejected"].includes(n.notification_type)) return "/app/customer-portal/my-payments";
    if (["emi_reminder", "emi_due_today", "emi_overdue"].includes(n.notification_type)) return "/app/customer-portal/pay";
    return null;
  };

  const handleNotifClick = (n) => {
    if (!n.is_read) markRead(n.notification_id);
    const url = getNotifUrl(n);
    if (url) {
      setOpen(false);
      window.location.href = url;
    }
  };

  if (!user) return null;

  return (
    <div className="relative">
      <button
        data-testid="notification-bell"
        onClick={handleOpen}
        className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-all"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] flex items-center justify-center px-1 bg-red-500 text-white text-[10px] font-bold rounded-full">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-2 w-80 max-h-96 bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden" data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h3 className="font-heading font-semibold text-slate-800 text-sm">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    data-testid="mark-all-read"
                    onClick={markAllRead}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  >
                    <CheckCheck size={12} /> Mark all read
                  </button>
                )}
                <button onClick={() => setOpen(false)}>
                  <X size={14} className="text-slate-400" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto max-h-72">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={18} className="animate-spin text-blue-600" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Bell size={24} className="mx-auto mb-2 text-slate-200" />
                  <p className="text-sm text-slate-400">No notifications</p>
                </div>
              ) : (
                notifications.map((n) => (
                  <div
                    key={n.notification_id}
                    data-testid={`notification-${n.notification_id}`}
                    className={`px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${
                      !n.is_read ? "bg-blue-50/50" : ""
                    }`}
                    onClick={() => handleNotifClick(n)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${typeIcon(n.notification_type)}`}>
                        <Bell size={12} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-xs font-semibold ${!n.is_read ? "text-slate-800" : "text-slate-600"}`}>{n.title}</p>
                          {!n.is_read && <span className="h-1.5 w-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
